import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LinearProgress } from '@mui/material'
import AvatarComponent from 'components/avatar'
import BackComponent from 'components/back'
import { SchoolIcon } from 'components/icons'
import { StarIcon } from 'components/icons/StarIcon'
import { TrophyIcon } from 'components/icons/TrophyIcon'
import SelectMobileComponent from 'components/select-mobile'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import React, { useEffect, useState } from 'react'
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom'
import { MessageProps } from 'types/CommonTypes'
import { CommunityCarpoolStatsOutputProps, TimePeriod, TimePeriodType } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UserOutputProps } from 'types/UserTypes'
import { getCommunityCarpoolStats } from 'utils/communities'
import { isWeb, titleCase, titleCaseSchoolNameForUI } from 'utils/constants'

const visibleTimePeriods = [TimePeriod.AllTime, TimePeriod.ToDate, TimePeriod.Next90Days]

const lookupTimePeriod = (tp: TimePeriodType) => {
  switch (tp) {
    case TimePeriod.AllTime:
      return 'All Time'
    case TimePeriod.Next365Days:
      return 'Next 365 Days'
    case TimePeriod.Next90Days:
      return 'Next 90 Days'
    case TimePeriod.Next30Days:
      return 'Next 30 Days'
    case TimePeriod.Next7Days:
      return 'Next 7 Days'
    case TimePeriod.Today:
      return 'Today'
    case TimePeriod.ToDate:
      return 'To Date'
    case TimePeriod.Last7Days:
      return 'Last 7 Days'
    case TimePeriod.Last30Days:
      return 'Last 30 Days'
    case TimePeriod.Last90Days:
      return 'Last 90 Days'
    case TimePeriod.Last365Days:
      return 'Last 365 Days'
    case TimePeriod.ThisMonth:
      return 'This Month'
    case TimePeriod.LastMonth:
      return 'Last Month'
    case TimePeriod.ThisYear:
      return 'This Year'
    case TimePeriod.LastYear:
      return 'Last Year'
    default:
      return tp
  }
}

const LEADERBOARD_DEFAULT_MAX_VISIBLE = 3
const LeaderboardRow = ({
  avatar,
  userName,
  studentNames = [],
  value,
  unit,
  isYou,
  failedPhotoLinks,
  onFailedPhotoLink,
  onClick,
}: {
  avatar: string
  userName: string
  studentNames?: string[]
  value: number
  unit: string
  isYou: boolean
  failedPhotoLinks: Set<string>
  onFailedPhotoLink: (url: string) => void
  onClick?: () => void
}) => (
  <button
    className={`w-full flex items-center p-4 rounded-lg text-left transition-colors space-x-3 ${
      isYou ? 'bg-yellow-50 border border-yellow-200' : 'hover:bg-gray-50'
    }`}
    onClick={onClick}
  >
    <div className='flex-shrink-0'>
      <AvatarComponent
        size={10}
        name={userName}
        photoLink={avatar}
        failedPhotoLinks={failedPhotoLinks}
        onFailedPhotoLink={onFailedPhotoLink}
      />
    </div>
    <div className='min-w-0 flex-1 overflow-hidden'>
      <p className='text-sm font-medium text-gray-900 truncate'>
        {userName} {isYou && <span className='text-yellow-600 font-normal'>(You)</span>}
      </p>
      {studentNames.length > 0 && (
        <div className='flex items-center gap-1 text-xs text-gray-600'>
          <div className='flex-shrink-0'>
            <SchoolIcon />
          </div>
          <span className='truncate'>
            {studentNames
              .filter(Boolean)
              .map((s) => titleCase(s).split(' ')[0])
              .join(', ')}
          </span>
        </div>
      )}
    </div>
    <div className='flex-shrink-0'>
      <p className='text-xl font-bold text-gray-900 whitespace-nowrap'>
        {value.toLocaleString()} <span className='text-gray-500 font-normal'>{unit}</span>
      </p>
    </div>
  </button>
)

// ========================================================================
// --- The Main Community Impact Page Component ---
// ========================================================================

const CommunityImpactComponent: React.FC<{
  studentId: string
  user: UserOutputProps
  userStudents: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}> = ({
  studentId: myStudentId,
  user: { phoneNumber: myUserId },
  userStudents,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}) => {
  // 1. State Hooks
  const navigate: NavigateFunction = useNavigate()
  const [timePeriod, setTimePeriod] = useState<TimePeriodType>(visibleTimePeriods[0])
  const [carpoolStats, setCarpoolStats] = useState<CommunityCarpoolStatsOutputProps>()
  const [showAllDrives, setShowAllDrives] = useState<boolean>(false)
  const [showAllRides, setShowAllRides] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<string>()
  const [msg, setMsg] = useState<MessageProps>()

  // 2. Derived Constants
  const hubUrl = `/hub?id=${myStudentId}`
  const student: StudentProps | undefined = userStudents.find((s: StudentProps) => s.studentId === myStudentId)
  const comId = student?.schoolName

  // 3. Effect Hooks
  useEffect(() => {
    if (!comId) return

    const loadData = async () => {
      setIsLoading('Loading community carpool stats...')
      // Always include 'today' along with the selected time period
      const timePeriods: TimePeriodType[] = [timePeriod, 'today']
      const res = await getCommunityCarpoolStats(comId, { poolTimePeriods: timePeriods })
      if (res.data) {
        setCarpoolStats(res.data.carpoolStats)
      }
      setIsLoading('')
    }

    loadData()
  }, [comId, timePeriod])

  // 4. Derived State, Validations and Early Returns
  if (!comId || !carpoolStats || !carpoolStats['today'] || !carpoolStats[timePeriod]) return <LinearProgress />
  const { totalDrives: todayTotalDrives, totalRides: todayTotalRides } = carpoolStats['today']
  const { totalDrives, totalRides, byUser } = carpoolStats[timePeriod]
  // Community Impact
  const allDrives = Object.entries(byUser).sort((a, b) => b[1].drivesCount - a[1].drivesCount)
  const allRides = Object.entries(byUser).sort((a, b) => b[1].ridesCount - a[1].ridesCount)
  const allDrivesLength = allDrives.length
  const allRidesLength = allRides.length
  const showAllDrivesToggle = allDrivesLength > LEADERBOARD_DEFAULT_MAX_VISIBLE
  const showAllRidesToggle = allRidesLength > LEADERBOARD_DEFAULT_MAX_VISIBLE
  const visibleDrivesCount = showAllDrivesToggle && !showAllDrives ? LEADERBOARD_DEFAULT_MAX_VISIBLE : allDrivesLength
  const visibleRidesCount = showAllRidesToggle && !showAllRides ? LEADERBOARD_DEFAULT_MAX_VISIBLE : allRidesLength
  const visibleDrives = allDrives.slice(0, visibleDrivesCount)
  const visibleRides = allRides.slice(0, visibleRidesCount)
  // Your Impact
  const yourDrivesCount = byUser[myUserId]?.drivesCount || 0
  const yourRidesCount = byUser[myUserId]?.ridesCount || 0
  const yourHoursSaved = (yourRidesCount - yourDrivesCount) * 1.5
  if (isLoading) return <LinearProgress />

  // 5. Event Handlers
  const onCancel = () => navigate(hubUrl)

  // 6. Render
  const rightElement: JSX.Element = (
    <SelectMobileComponent
      isRequired={true}
      options={visibleTimePeriods.map((tp: TimePeriodType) => ({
        key: tp,
        value: tp,
        label: lookupTimePeriod(tp),
      }))}
      value={timePeriod}
      onChange={(v: string) => setTimePeriod(v as TimePeriodType)}
    />
  )
  const body: JSX.Element = (
    <div className={`flex flex-col flex-1 self-stretch gap-8 ${isWeb ? '' : 'px-5'}`}>
      {/* --- Section 1: Combining personal impact and today's call to action --- */}
      <div className='bg-white p-4 rounded-lg shadow-sm border'>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
          {/* Your Impact Card */}
          <div className='p-4 rounded-xl bg-yellow-100 border-2 border-yellow-400'>
            <h2 className='font-bold text-lg text-gray-900'>Your Impact ({lookupTimePeriod(timePeriod)})</h2>
            <div className='mt-3 space-y-2'>
              <div className='flex justify-between items-baseline'>
                <span className='text-gray-900'>Drives Completed</span>
                <span className='font-bold text-2xl text-gray-900'>{yourDrivesCount}</span>
              </div>
              <div className='flex justify-between items-baseline'>
                <span className='text-gray-900'>Rides Provided</span>
                <span className='font-bold text-2xl text-gray-900'>{yourRidesCount}</span>
              </div>
              <div className='pt-2 border-t border-yellow-300 mt-3'>
                {yourHoursSaved > 0 ? (
                  <p className='text-sm text-gray-800 text-center'>
                    {`You've saved ${titleCaseSchoolNameForUI(comId)} community an estimated `}
                    <span className='font-bold text-gray-900'>{yourHoursSaved.toFixed(1)} hours</span> by carpooling.
                  </p>
                ) : (
                  <p className='text-sm text-gray-800 text-center'>
                    Join a carpool today to start saving time and helping your community!
                  </p>
                )}
              </div>
            </div>
          </div>
          {/* Today's Snapshot Card */}
          <div className='p-4 rounded-xl bg-yellow-50 border-2 border-yellow-300 text-center'>
            <p className='font-bold text-gray-900'>{`Today's Snapshot`}</p>
            <p className='text-sm text-gray-800 font-medium'>
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
            </p>
            <div className='mt-3 grid grid-cols-2 gap-2'>
              <div>
                <p className='font-bold text-2xl text-gray-900'>{todayTotalDrives}</p>
                <p className='text-sm text-gray-800'>Families Driving</p>
              </div>
              <div>
                <p className='font-bold text-2xl text-gray-900'>{todayTotalRides}</p>
                <p className='text-sm text-gray-800'>Students Riding</p>
              </div>
            </div>
            {/* <button
              onClick={() => navigate(`/carpool/management?id=${myStudentId}`)}
              className='mt-4 w-full py-2.5 px-4 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-200'
            >
              View or Join a Carpool
            </button> */}
          </div>
        </div>
      </div>

      {/* --- Section 2: Top Drivers Leaderboard --- */}
      <div className='bg-white p-4 rounded-lg shadow-sm border'>
        {/* --- Leaderboard Header --- */}
        <div className='flex items-start justify-between pb-3 border-b border-gray-200'>
          <h2 className='text-lg font-bold text-gray-800 flex items-center gap-2'>
            <TrophyIcon /> Top Drivers
          </h2>
          <div className='text-right'>
            <p className='text-sm font-bold text-gray-600'>Carpools Completed</p>
            <p className='text-3xl font-bold text-gray-800'>{totalDrives}</p>
          </div>
        </div>
        {/* --- Leaderboard Body --- */}
        <div className={`${showAllDrives ? 'space-y-1 mt-3' : 'space-y-1 mt-2 animate-fade-in'}`}>
          {visibleDrives.map(([driverId, metrics], index) => {
            const isYou = driverId === myUserId
            return (
              <LeaderboardRow
                key={index}
                avatar={'https://via.placeholder.com/150'}
                userName={metrics.userName}
                value={metrics.drivesCount}
                unit='drives'
                isYou={isYou}
                failedPhotoLinks={failedAvatarUrls}
                onFailedPhotoLink={onFailedAvatarUrl}
                onClick={() => navigate(`/userpool/${comId}?studentId=${myStudentId}`, { state: { userId: driverId } })}
              />
            )
          })}
          {showAllDrivesToggle && (
            <button
              onClick={() => setShowAllDrives((prev) => !prev)}
              className='w-full text-center text-sm font-bold text-blue-600 hover:underline p-2 flex items-center justify-center gap-1'
            >
              {showAllDrives ? 'Show Less' : `Show all ${allDrivesLength} drivers`}{' '}
              <ExpandMoreIcon className={`transition-transform ${showAllDrives ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>

      {/* --- Section 3: Top Carpooling Families Leaderboard --- */}
      <div className='bg-white p-4 rounded-lg shadow-sm border'>
        {/* --- Leaderboard Header --- */}
        <div className='flex items-start justify-between pb-3 border-b border-gray-200'>
          <h2 className='text-lg font-bold text-gray-800 flex items-center gap-2'>
            <StarIcon /> Top Families
          </h2>
          <div className='text-right'>
            <p className='text-sm font-bold text-gray-600'>Single-Car Trips Reduced</p>
            <p className='text-3xl font-bold text-gray-800'>{totalRides}</p>
          </div>
        </div>
        {/* --- Leaderboard Body --- */}
        <div className={`${showAllRides ? 'space-y-1 mt-3' : 'space-y-1 mt-2 animate-fade-in'}`}>
          {visibleRides.map(([driverId, metrics], index) => {
            const isYou = driverId === myUserId
            return (
              <LeaderboardRow
                key={index}
                avatar={'https://via.placeholder.com/150'}
                userName={metrics.userName}
                studentNames={Object.values(metrics.byStudent).map((s) => s.studentName)}
                value={metrics.ridesCount}
                unit='rides'
                isYou={isYou}
                failedPhotoLinks={failedAvatarUrls}
                onFailedPhotoLink={onFailedAvatarUrl}
                onClick={() => navigate(`/userpool/${comId}?studentId=${myStudentId}`, { state: { userId: driverId } })}
              />
            )
          })}
          {showAllRidesToggle && (
            <button
              onClick={() => setShowAllRides((prev) => !prev)}
              className='w-full text-center text-sm font-bold text-blue-600 hover:underline p-2 flex items-center justify-center gap-1'
            >
              {showAllRides ? 'Show Less' : `Show all ${allRidesLength} families`}{' '}
              <ExpandMoreIcon className={`transition-transform ${showAllRides ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
  const footer: JSX.Element | null = null

  return (
    <div className='flex flex-col min-h-screen'>
      {/***************** Display progress and error *****************/}
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}
      {!isWeb ? (
        <div className='flex flex-col flex-1'>
          <div className='sticky top-0 z-10 bg-white'>
            <BackComponent text={'Community Impact'} onClick={onCancel} rightElement={rightElement} />
          </div>
          <div className={`flex-1 ${footer ? 'pb-24' : 'pb-4'}`}>{body}</div>
          {footer && (
            <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pt-3 pb-7 px-5'>
              {footer}
            </div>
          )}
        </div>
      ) : (
        <WizardComponent onBack={onCancel} onCancel={onCancel} title='Community Impact' rightElement={rightElement}>
          <>
            {body}
            {footer}
          </>
        </WizardComponent>
      )}
    </div>
  )
}

function Index({
  user,
  students,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}: {
  user?: UserOutputProps
  students: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}): JSX.Element {
  const [searchParams] = useSearchParams()

  // Build props for child component
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (!user || !studentId || !student) return <LinearProgress />
  return (
    <CommunityImpactComponent
      studentId={studentId}
      user={user}
      userStudents={students}
      onUpdate={onUpdate}
      failedAvatarUrls={failedAvatarUrls}
      onFailedAvatarUrl={onFailedAvatarUrl}
    />
  )
}

export default Index
